import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase';
import { setTokenGetter } from "../api/sync";

const AuthContext = createContext(null);

function isNative() {
  return typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();
}

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(undefined); // undefined = still initializing

  // MUST be in render body — child effects can fire before parent effects on cached sessions
  setTokenGetter(firebaseUser ? async (forceRefresh = false) => {
    try {
      return await firebaseUser.getIdToken(Boolean(forceRefresh));
    } catch {
      return await firebaseUser.getIdToken();
    }
  } : null);

  useEffect(() => {
    return onAuthStateChanged(auth, setFirebaseUser);
  }, []);

  // Both providers used to swallow errors on native (console.error + alert())
  // while the web path had no error handling at all — a failure there was a
  // silently-unhandled promise rejection, invisible to the user and to
  // anyone debugging from a bug report. Now both paths let the real Firebase
  // error (with its .code) propagate to the caller, which is responsible for
  // showing it — see LandingPage's handleGoogleLogin/handleAppleLogin.
  const loginWithGoogle = async () => {
    if (isNative()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle();
      if (result.credential?.idToken) {
        const credential = GoogleAuthProvider.credential(result.credential.idToken);
        await signInWithCredential(auth, credential);
      }
    } else {
      await signInWithPopup(auth, new GoogleAuthProvider());
    }
  };

  const loginWithApple = async () => {
    if (isNative()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithApple();
      if (result.credential?.idToken) {
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
          idToken:  result.credential.idToken,
          rawNonce: result.credential.nonce,
        });
        await signInWithCredential(auth, credential);
      }
    } else {
      await signInWithPopup(auth, new OAuthProvider('apple.com'));
    }
  };

  const loginWithEmail = async (email, password, isNewUser = false) => {
    try {
      if (isNewUser) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (e) {
      throw e;
    }
  };

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const logout = () => firebaseSignOut(auth);

  const mappedUser = firebaseUser ? {
    userDetails:      firebaseUser.email || firebaseUser.uid,
    identityProvider: firebaseUser.providerData?.[0]?.providerId || 'firebase',
    userId:           firebaseUser.uid,
  } : null;

  return (
    <AuthContext.Provider value={{
      user:           mappedUser,
      firebaseUser:   firebaseUser || null,
      loading:        firebaseUser === undefined,
      sessionExpired: false,
      login:          loginWithGoogle,
      loginWithGoogle,
      loginWithApple,
      loginWithEmail,
      resetPassword,
      refreshSession: loginWithGoogle,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
